import { useState, useEffect, useRef, useId, type ReactNode } from "react";
import type { Variants } from "motion/react";
import { stagger } from "motion/react";
import * as motion from "motion/react-client";
import {
  FireIcon,
  GreenSenseiIcon,
  CodeIcon,
  FileTextIcon,
  QuizIcon,
  SettingsIcon,
} from "../../base/Icon";
import { Button } from "../../base/Button";
// ご意見ボックスは一時非表示。復活させるときはこの import も戻す
// import { FeedbackLink } from "../../base/FeedbackLink";
import {
  UserProfileMenu,
  UserAvatarButton,
} from "../../common/UserProfileMenu";
import { Tooltip } from "../../base/Tooltip";
import { LanguageIcon } from "../../base/Icon";

// Icon Components (IconList removed as we're using animated hamburger menu)

/**
 * ゆめさくの先頭の印。パンくずの前に置いて、レッスンの中にいても
 * どこのサイトかが分かるようにする。
 *
 * 形は favicon と同じ「ゆ」と双葉。色は元の黄緑と黒ではなく、
 * 黄緑から緑への流れにしてある。帯の中で 1 つだけ色が濃い点になり、
 * パンくずの文字と競らない
 */
/** 印のリンク。押せる面を 44px にするのはこのファイルの決まり */
const MARK_LINK_CLASS =
  "flex h-11 min-w-11 shrink-0 items-center justify-center no-underline transition-transform hover:scale-110";

function YumesakuMark({ className }: { className?: string }) {
  /*
    同じ画面に 2 つ出ても塗りが入れ替わらないように、id は都度作る。
    **useId の値をそのまま url(#...) に入れない。** React 19.1 は
    `«r0»` のような非 ASCII を返し、これは XML の名前として無効で、
    参照が解けずに塗りが消えるブラウザがある。使える字だけ残す
  */
  const gradientId = `yumesaku-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      viewBox="-6 2 129 180"
      className={className}
      /* 名乗りは包んでいるリンク側に置く。両方に付けると、読み上げが
         リンク名のあとに画像名まで続けて読む */
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A6D62B" />
          <stop offset="55%" stopColor="#3FB93F" />
          <stop offset="100%" stopColor="#05B45B" />
        </linearGradient>
      </defs>
      <path
        d="M77.4778 178L73.3739 160.543C79.9401 160.361 84.6823 159.401 87.6462 157.756C92.2517 155.608 94.3948 151.495 94.3948 145.737V128.966C94.3948 116.947 88.467 111.052 76.3378 111.052H69.27V129.788C69.27 149.713 59.1015 159.721 38.5822 159.721C38.263 159.904 34.4783 159.904 27.4105 159.721C17.8805 159.538 10.3567 157.07 5.06726 151.998C-0.222173 146.925 -2.82129 139.476 -2.82129 129.971V124.03C-2.82129 104.929 6.52641 94.9206 25.085 93.7782V73.5339H41.6829V93.2755L51.7146 93.0927V34.0049H69.27V93.1384L76.3378 93.3212C87.8286 93.504 96.538 96.6115 102.603 102.369C108.986 108.63 112.133 117.358 112.133 128.509V145.463C112.133 166.347 100.459 177.223 77.4778 178.046V178ZM51.7146 111.052H41.6829V136.049H25.085V111.189C18.1996 112.195 14.7341 116.307 14.7341 123.345V129.286C14.7341 137.831 19.3396 142.127 28.3681 142.127H37.3966C42.3213 142.127 45.9236 141.122 48.2491 139.157C50.529 137.192 51.7146 133.718 51.7146 128.783V111.006V111.052Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M60.2393 33.91C62.8779 27.2479 52.892 17.0239 37.935 11.0739C22.978 5.12394 8.71398 5.70122 6.07532 12.3633C3.43666 19.0254 13.4226 29.2494 28.3796 35.1994C43.3365 41.1493 57.6006 40.5721 60.2393 33.91Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M92.4328 35.19C107.39 29.2401 117.376 19.016 114.737 12.3539C112.098 5.69185 97.8343 5.11457 82.8774 11.0645C67.9204 17.0145 57.9345 27.2386 60.5731 33.9006C63.2118 40.5627 77.4758 41.14 92.4328 35.19Z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
}

function IconAngleRight({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9 6L15 12L9 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconUsersAlt({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 14V12.6667C12 11.9594 11.719 11.2811 11.219 10.781C10.7189 10.281 10.0406 10 9.33333 10H4.66667C3.95942 10 3.28115 10.281 2.78105 10.781C2.28095 11.2811 2 11.9594 2 12.6667V14M14 14V12.6667C13.9997 12.0758 13.8092 11.5019 13.4567 11.0349C13.1043 10.5679 12.6089 10.2344 12.0467 10.086M10.38 2.086C10.9437 2.23316 11.4406 2.56674 11.7942 3.03448C12.1477 3.50222 12.3387 4.07733 12.3387 4.66933C12.3387 5.26134 12.1477 5.83645 11.7942 6.30419C11.4406 6.77193 10.9437 7.10551 10.38 7.25267M9.33333 4.66667C9.33333 6.13943 8.13943 7.33333 6.66667 7.33333C5.19391 7.33333 4 6.13943 4 4.66667C4 3.19391 5.19391 2 6.66667 2C8.13943 2 9.33333 3.19391 9.33333 4.66667Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface CodingLessonBreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  /** 学習パスの項目へ戻る入口。パンくずの列には混ぜず、帯の左に別に出す (スマホの幅でも見える) */
  back?: boolean;
}

export interface CodingLessonHeaderProps {
  /**
   * パンくずリストの項目
   */
  breadcrumbs: CodingLessonBreadcrumbItem[];
  /**
   * 帯の左に「どこまで来たか」を絵で出す。渡すとパンくずの字の列を
   * この形に差し替える。**渡さない画面は今までどおり。**
   */
  courseTrail?: { current: number; total: number };

  /**
   * 勉強中の人数
   */
  studyingCount?: number;

  /**
   * ユーザー名
   */
  userName?: string;

  /**
   * ログイン中ユーザーのメールアドレス
   */
  userEmail?: string;

  /**
   * メニューアイコンクリック時のハンドラー
   */
  onMenuClick?: () => void;
  onMenuNavigate?: () => void;
  menuActionLabel?: string;
  menu?: { title: string; content: ReactNode };
  /** 左端のメニューの釦を出すか。既定は出す。公開の問題の頁は、並びを左のサイドバーに持つので出さない */
  showMenuButton?: boolean;

  /**
   * 勉強中ボタンクリック時のハンドラー
   */
  onStudyingClick?: () => void;

  /**
   * ユーザーアバタークリック時のハンドラー
   */
  onUserClick?: () => void;

  /**
   * ユーザーレベル
   */
  userLevel?: number;

  /**
   * ユーザーランク (C, C+, B, B+, A, A+, S, S+)
   */
  userRank?: string;

  /**
   * 合計XP
   */
  userTotalXp?: number;

  /**
   * 次のレベルまでのXP
   */
  userXpToNextLevel?: number;

  /**
   * プロフィールメニューのクリックハンドラ
   */
  onProfileClick?: () => void;

  /**
   * マイプランのクリックハンドラ
   */
  onPlanClick?: () => void;

  /**
   * ヘルプ・サポートのクリックハンドラ
   */
  onHelpClick?: () => void;

  /**
   * ログアウトのクリックハンドラ
   */
  onLogoutClick?: () => void;

  /**
   * 追加のクラス名
   */
  className?: string;

  /**
   * コース情報（メニュー表示用）
   */
  course?: {
    title: string;
    description?: string;
    mark?: { language: string; alt: string };
    sections: Array<{
      stepNumber: number;
      title: string;
      lessons: Array<{
        id: string;
        number: string;
        title: string;
        duration: number;
        type: "text" | "code" | "quiz";
        status: "completed" | "current" | "available" | "locked";
        slug?: string;
      }>;
      defaultOpen?: boolean;
    }>;
  };

  /**
   * コーススラッグ（レッスンリンク用）
   */
  courseSlug?: string;

  /**
   * リンクコンポーネント（React Router用）
   */
  LinkComponent?: any;

  /**
   * レッスンクリック時のハンドラー
   */
  onLessonClick?: (lessonId: string) => void;

  /**
   * ブックマーク状態
   */
  isBookmarked?: boolean;

  /**
   * ブックマーク切り替え時のハンドラー
   */
  onBookmarkToggle?: () => void;

  /**
   * メモボタンクリック時のハンドラー
   */
  onNotesClick?: () => void;

  /**
   * メモが存在するかどうか
   */
  hasNotes?: boolean;

  /**
   * レッスンがクリア済みかどうか
   */
  isLessonCompleted?: boolean;

  /**
   * 帯の右側クラスタの先頭に差し込む面 (ゆめさくコーチのつまみ等)。
   * 状態は呼び出し側が持つ。渡さない画面は今までどおり
   */
  headerAction?: ReactNode;

  /**
   * 設定のつまみ (図解の自動再生) を出すか。既定は出す。
   * 図解の無い頁 (コーディング面接問題など) では、押しても何も変わらないので false にする
   */
  showSettings?: boolean;
}

// Sidebar variants for animation
const sidebarVariants: Variants = {
  open: {
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  closed: {
    x: "-100%",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
};

// Navigation variants
const navVariants: Variants = {
  open: {
    transition: { delayChildren: stagger(0.07, { startDelay: 0.2 }) },
  },
  closed: {
    transition: { delayChildren: stagger(0.05, { from: "last" }) },
  },
};

// Menu item variants
const itemVariants: Variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
};

// Path component for hamburger icon animation
interface PathProps {
  d?: string;
  variants: Variants;
  transition?: { duration: number };
}

const Path = (props: PathProps) => (
  <motion.path
    fill="transparent"
    strokeWidth="3"
    stroke="currentColor"
    strokeLinecap="round"
    {...props}
  />
);

export function CodingLessonHeader({
  breadcrumbs: allBreadcrumbs,
  courseTrail,
  studyingCount,
  userName,
  userEmail,
  onMenuNavigate,
  menuActionLabel,
  menu,
  showMenuButton = true,
  onStudyingClick,
  onUserClick,
  userLevel,
  userRank,
  userTotalXp,
  userXpToNextLevel,
  onProfileClick,
  onPlanClick,
  onHelpClick,
  onLogoutClick,
  className = "",
  course,
  courseSlug,
  LinkComponent,
  onLessonClick,
  isBookmarked,
  onBookmarkToggle,
  onNotesClick,
  hasNotes,
  isLessonCompleted,
  headerAction,
  showSettings = true,
}: CodingLessonHeaderProps) {
  // 学習パスから来たときの戻り口は、パンくずと道の絵には混ぜない
  const backCrumb = allBreadcrumbs.find((item) => item.back && item.href);
  const breadcrumbs = allBreadcrumbs.filter((item) => !item.back);
  /*
  いま居る場所を戻り先として付ける。

  **描画の途中で window.location を読まない。** サーバーは素の /login を
  出し、ブラウザは ?redirect 付きを出すので href が食い違って hydration が
  警告を出す。effect で入れ直す。最初の 1 フレームだけ戻り先の無い
  リンクになるが、そこを押せる時間は実質無い
  */
  const [authRedirect, setAuthRedirect] = useState("");
  useEffect(() => {
    const here = window.location.pathname + window.location.search;
    if (!here || here === "/" || here.startsWith("/login") || here.startsWith("/signup")) {
      return;
    }
    setAuthRedirect(`?redirect=${encodeURIComponent(here)}`);
  }, []);
  const withRedirect = (path: string) => `${path}${authRedirect}`;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasCustomMenu = menu !== undefined;
  const menuId = useId();
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // 図解の自動再生。既定は入なので、切ったときだけ localStorage に "off" が残る
  const [figureAutoplay, setFigureAutoplayState] = useState(true);

  useEffect(() => {
    try {
      setFigureAutoplayState(localStorage.getItem("figure-autoplay") !== "off");
    } catch {
      // 読めない環境では既定の入のままにする
    }
  }, []);

  const setFigureAutoplay = (next: boolean) => {
    setFigureAutoplayState(next);
    try {
      if (next) localStorage.removeItem("figure-autoplay");
      else localStorage.setItem("figure-autoplay", "off");
    } catch {
      // 記録できなくてもこの画面のあいだは切り替わる
    }
  };
  const [openSections, setOpenSections] = useState<Set<number>>(
    new Set(course?.sections.map((s) => s.stepNumber) || [])
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasCustomMenu || !isMenuOpen) return;
    const drawer = containerRef.current;
    if (!drawer) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const focusableElements = () => [...drawer.querySelectorAll<HTMLElement>(
      "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex='0']",
    )];
    const focusFirst = () => (focusableElements()[0] ?? drawer).focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setIsMenuOpen(false);
      } else if (event.key === "Tab") {
        const elements = focusableElements();
        const first = elements[0];
        const last = elements.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Node && !drawer.contains(event.target)) focusFirst();
    };
    document.body.style.overflow = "hidden";
    focusFirst();
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocusIn);
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [hasCustomMenu, isMenuOpen]);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target as Node)
      ) {
        setIsSettingsMenuOpen(false);
      }
    };
    if (isSettingsMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSettingsMenuOpen]);

  const handleMenuToggle = () => {
    if (onMenuNavigate) {
      onMenuNavigate();
      return;
    }
    setIsMenuOpen((prev) => !prev);
  };

  // 勉強中メッセージとアイコンのローテーション
  const studyingItems =
    studyingCount !== undefined
      ? [
          {
            icon: <IconUsersAlt className="w-4 h-4" />,
            message: `${studyingCount}人が勉強中`,
          },
          {
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.00098 2.33301C6.88503 2.33301 7.73328 2.68445 8.3584 3.30957C8.98342 3.93468 9.33398 4.78301 9.33398 5.66699C9.33394 6.13735 9.23261 6.60246 9.03711 7.03027C8.8416 7.45794 8.55669 7.8387 8.20117 8.14648C9.13432 8.56905 9.92581 9.25172 10.4814 10.1123C11.0371 10.973 11.333 11.9755 11.334 13C11.334 13.1768 11.2637 13.3467 11.1387 13.4717C11.0138 13.5964 10.8445 13.6669 10.668 13.667C10.4912 13.667 10.3213 13.5966 10.1963 13.4717C10.0713 13.3467 10.001 13.1768 10.001 13C10.001 11.9391 9.57925 10.922 8.8291 10.1719C8.07896 9.42173 7.06184 9 6.00098 9C4.94011 9 3.923 9.42173 3.17285 10.1719C2.42271 10.922 2.00098 11.9391 2.00098 13C2.00098 13.1768 1.93069 13.3467 1.80566 13.4717C1.68066 13.5966 1.51072 13.667 1.33398 13.667C1.15744 13.6669 0.988197 13.5964 0.863281 13.4717C0.738257 13.3467 0.667969 13.1768 0.667969 13C0.66895 11.9755 0.964851 10.973 1.52051 10.1123C2.07614 9.25172 2.86763 8.56905 3.80078 8.14648C3.44526 7.8387 3.16035 7.45794 2.96484 7.03027C2.76934 6.60246 2.66801 6.13735 2.66797 5.66699C2.66797 4.78301 3.01854 3.93468 3.64355 3.30957C4.26868 2.68445 5.11692 2.33301 6.00098 2.33301ZM10.001 2.33301C10.6433 2.33284 11.2721 2.51848 11.8115 2.86719C12.351 3.21606 12.7785 3.71385 13.042 4.2998C13.3054 4.88585 13.3941 5.53575 13.2969 6.1709C13.1997 6.80604 12.9208 7.39944 12.4941 7.87988C13.3617 8.42209 14.0767 9.17711 14.5703 10.0732C15.0639 10.9694 15.3199 11.977 15.3145 13C15.3145 13.1768 15.2442 13.3467 15.1191 13.4717C14.9941 13.5966 14.8242 13.667 14.6475 13.667C14.4707 13.6669 14.3008 13.5967 14.1758 13.4717C14.0508 13.3467 13.9814 13.1768 13.9814 13C13.9885 12.1107 13.7409 11.2379 13.2686 10.4844C12.7962 9.73091 12.1179 9.12822 11.3145 8.74707L11.2275 8.7002L10.9678 8.52637C10.8733 8.46419 10.7956 8.37948 10.7432 8.2793C10.6907 8.17899 10.6652 8.0663 10.668 7.95312C10.671 7.83923 10.7025 7.72781 10.7607 7.62988C10.8191 7.53186 10.9022 7.45057 11.001 7.39355C11.3041 7.21856 11.556 6.96688 11.7314 6.66406C11.9069 6.36114 12 6.01704 12.001 5.66699C12.001 5.13664 11.79 4.62799 11.415 4.25293C11.04 3.87786 10.5314 3.66699 10.001 3.66699C9.82417 3.66699 9.65432 3.5967 9.5293 3.47168C9.40427 3.34666 9.33398 3.17681 9.33398 3C9.33398 2.82319 9.40427 2.65334 9.5293 2.52832C9.65432 2.4033 9.82417 2.33301 10.001 2.33301ZM6.7666 3.81934C6.40115 3.66796 5.99831 3.62791 5.61035 3.70508C5.22255 3.7823 4.86652 3.97333 4.58691 4.25293C4.30731 4.5326 4.11622 4.8885 4.03906 5.27637C3.96191 5.66422 4.00205 6.06627 4.15332 6.43164C4.3047 6.79709 4.56075 7.11032 4.88965 7.33008C5.21853 7.5498 5.60545 7.66699 6.00098 7.66699C6.53133 7.66699 7.03998 7.45603 7.41504 7.08105C7.79004 6.70606 8.00089 6.19731 8.00098 5.66699C8.00098 5.2715 7.88376 4.88453 7.66406 4.55566C7.44437 4.22688 7.13189 3.97073 6.7666 3.81934Z"
                  fill="#727270"
                />
              </svg>
            ),
            message: "みんなで学んでいます",
          },
          {
            icon: <FireIcon />,
            message: "4日連続で勉強中",
          },
        ]
      : [];
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (studyingItems.length === 0) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentItemIndex((prev) => (prev + 1) % studyingItems.length);
        setIsAnimating(false);
      }, 750); // アニメーション時間の半分
    }, 30000); // 30秒ごとに切り替え

    return () => clearInterval(interval);
  }, [studyingItems.length]);
  /*
  # 帯の左の「道」

  以前はここに `コース一覧 > Webセキュリティ入門：HTTP・Cookie・脆弱性 >
  セキュリティとは何か` と字が 3 つ並んでいた。**3 つとも読まないと
  どこに居るか分からないし、読んでも「何本目か」は書いていない。**

  絵にする。もぐらが道の上に居て、道の長さがコース 1 本ぶん。
  点は章の切れ目。**もぐらの位置は current/total そのまま**なので、
  進むと右へ動く。

  コース名は 1 つだけ残す。**「：」の前だけ出す。** 副題まで出すと
  帯の半分を使い、隣の題と見分けが付かなくなる。押すとコース詳細へ行く
  ので、道を戻る手段は残っている。「コース一覧」は引き出しの中にある。
  */
  const renderCourseTrail = () => {
    if (!courseTrail || courseTrail.total <= 0) return null;
    const { current, total } = courseTrail;
    const ratio = Math.min(1, Math.max(0, (current - 1) / Math.max(1, total - 1)));
    /* コース名の本体。副題は落とす */
    const course = breadcrumbs[breadcrumbs.length - 2];
    const name = (course?.label ?? "").split(/[：:]/)[0];

    const inner = (
      <span className="flex min-w-0 items-center gap-2.5">
        {/* 道。もぐらが左端から右端へ歩く */}
        <span
          aria-hidden="true"
          className="relative hidden h-7 w-[72px] shrink-0 items-center lg:flex"
        >
          <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-third" />
          {[0.34, 0.67, 1].map((at) => (
            <span
              key={at}
              style={{ left: `calc(${at * 100}% - 3px)` }}
              className={`absolute top-1/2 h-[6px] w-[6px] -translate-y-1/2 rounded-full ${
                ratio >= at ? "bg-primary" : "bg-third"
              }`}
            />
          ))}
          <img
            src="/mascot/mogura-01.svg"
            alt=""
            width={26}
            height={27}
            /* 道の内側だけを歩かせる。`ratio% - 13px` にすると、
               先頭では左へ 13px はみ出し、終点では右の題に重なる */
            style={{ left: `calc(${ratio} * (100% - 26px))` }}
            className="absolute top-1/2 -translate-y-1/2 transition-[left] duration-500 ease-out motion-reduce:transition-none"
          />
        </span>

        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate font-sans max-sm:text-[0.9375rem] text-[13px] font-bold text-main">
            {name}
          </span>
          <span className="font-sans max-sm:text-[0.9375rem] text-[11px] text-sub">
            {current} / {total}
          </span>
        </span>
      </span>
    );

    return course?.href ? (
      <a
        href={course.href}
        /* 見えている「1 / 36」は現在地。帯の下の「読了 N / 36」は
           読み終えた数で、別のもの。読み上げには取り違えないよう書く */
        aria-label={`${name} コース全 ${total} 本のうち ${current} 本目`}
        className="flex min-h-11 min-w-0 items-center rounded-lg px-1 -mx-1 no-underline transition-colors hover:text-primary"
      >
        {inner}
      </a>
    ) : (
      inner
    );
  };

  const renderBreadcrumbs = () => {
    return breadcrumbs.map((item, index) => {
      const isLast = index === breadcrumbs.length - 1;
      // モバイルでは最後の2つだけ表示
      const hiddenOnMobile = index < breadcrumbs.length - 2 ? "hidden md:flex" : "flex";

      return (
        <div key={index} className={`${hiddenOnMobile} items-center`}>
          <div className="flex items-center gap-0.5">
            {item.href || item.onClick ? (
              <a
                href={item.href || "#"}
                onClick={(e) => {
                  if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                /* 字は 11px で高さも 11px しか無く、押せる面が線一本だった。
                   入れ物の DIV が既に 44px あるので、リンク側を min-h-11 に
                   すると帯の高さを変えずに面だけ広がる */
                className={`inline-flex min-h-11 items-center rounded px-1 -mx-1 font-sans max-sm:text-[0.9375rem] text-[12px] font-normal leading-none no-underline transition-colors duration-150 ${
                  isLast
                    ? "text-primary hover:text-primary/80"
                    : "text-sub hover:text-primary"
                }`}
              >
                {item.label}
              </a>
            ) : (
              <span
                className={`font-sans max-sm:text-[0.9375rem] text-[12px] font-normal leading-none ${
                  isLast ? "text-primary" : "text-sub"
                }`}
              >
                {item.label}
              </span>
            )}
            {!isLast && <IconAngleRight className="w-4 h-4 text-sub" />}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <header
        className={`flex items-center justify-between px-4 bg-main border-b border-main h-12 ${className}`}
      >
        <div className="flex items-center gap-1 sm:gap-2">
          {showMenuButton ? (
          <motion.button
            /* 絵は 20px のままで、押せる面だけ 44px にする。帯は 48px
               なので入る。以前は 24x20 で、**これはページでいちばん
               最初に押すものの大きさとして小さすぎた** */
            className="flex h-11 w-11 -ml-2 items-center justify-center bg-transparent border-none cursor-pointer text-primary p-0"
            onClick={handleMenuToggle}
            aria-label={menuActionLabel ?? (isMenuOpen ? "メニューを閉じる" : "メニューを開く")}
            aria-expanded={menu ? isMenuOpen : undefined}
            aria-controls={menu ? menuId : undefined}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              initial={false}
              animate={isMenuOpen ? "open" : "closed"}
            >
              <svg
                y="3"
                width="20"
                height="14"
                viewBox="0 0 20 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.00977 12C1.56205 12 2.00977 12.4477 2.00977 13C2.00977 13.5523 1.56205 14 1.00977 14H1C0.447715 14 0 13.5523 0 13C0 12.4477 0.447715 12 1 12H1.00977ZM19 12C19.5523 12 20 12.4477 20 13C20 13.5523 19.5523 14 19 14H6C5.44772 14 5 13.5523 5 13C5 12.4477 5.44772 12 6 12H19ZM1.00977 6C1.56205 6 2.00977 6.44772 2.00977 7C2.00977 7.55228 1.56205 8 1.00977 8H1C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H1.00977ZM19 6C19.5523 6 20 6.44772 20 7C20 7.55228 19.5523 8 19 8H6C5.44772 8 5 7.55228 5 7C5 6.44772 5.44772 6 6 6H19ZM1.00977 0C1.56205 0 2.00977 0.447715 2.00977 1C2.00977 1.55228 1.56205 2 1.00977 2H1C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H1.00977ZM19 0C19.5523 0 20 0.447715 20 1C20 1.55228 19.5523 2 19 2H6C5.44772 2 5 1.55228 5 1C5 0.447715 5.44772 0 6 0H19Z"
                  fill="currentColor"
                />
              </svg>
            </motion.svg>
          </motion.button>
          ) : null}
          {/* 帯の左は「開く」と「戻る」だけだった。どこのサイトかを出す。
              押せる面は 44px。絵は 24px でも、指で押す面はこのファイルの
              他のボタンと同じ大きさにする */}
          {LinkComponent ? (
            <LinkComponent
              to="/"
              aria-label="ゆめさくコード トップへ"
              className={MARK_LINK_CLASS}
            >
              <YumesakuMark className="h-6 w-auto md:h-7" />
            </LinkComponent>
          ) : (
            <a href="/" aria-label="ゆめさくコード トップへ" className={MARK_LINK_CLASS}>
              <YumesakuMark className="h-6 w-auto md:h-7" />
            </a>
          )}
          {backCrumb ? (
            <a
              href={backCrumb.href}
              data-testid="lesson-path-back"
              onClick={(event) => {
                // 新しいタブや右クリックはリンクのまま。普通の左クリックだけ呼び手に渡す
                if (
                  !backCrumb.onClick ||
                  event.defaultPrevented ||
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                backCrumb.onClick();
              }}
              className="inline-flex min-h-11 shrink-0 items-center gap-0.5 rounded px-1 font-sans max-sm:text-[0.9375rem] text-[12px] font-bold leading-none text-primary no-underline transition-colors duration-150 hover:text-primary/80"
            >
              <IconAngleRight className="h-4 w-4 rotate-180" />
              {backCrumb.label}
            </a>
          ) : null}
          <nav
            className="hidden md:flex flex-wrap items-center gap-x-1"
            aria-label="パンくずリスト"
          >
            {courseTrail ? renderCourseTrail() : renderBreadcrumbs()}
            {isLessonCompleted && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full max-sm:text-[0.9375rem] text-[10px] font-bold bg-[#05b45b]/10 text-[#05b45b] border border-[#05b45b]/20">
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                クリア済み
              </span>
            )}
          </nav>
        </div>

        <div className="flex items-center h-full">
          {/* 0. 呼び出し側が差し込む面 (ゆめさくコーチのつまみ等) */}
          {headerAction}
          {/* 1. みんなで学んでいます */}
          {studyingCount !== undefined && studyingItems.length > 0 && (
            /*
            押せる顔をするのは、押して何かが起きるときだけ。**未ログインでは
            onStudyingClick が無く、押しても「受講管理を開く」の吹き出しが
            出るだけだった。** 押せないなら吹き出しも出さず、字だけ置く
            */
            <Tooltip
              content={onStudyingClick ? "受講管理を開く" : ""}
              position="bottom-center"
            >
              <button
                disabled={!onStudyingClick}
                aria-disabled={!onStudyingClick}
                className={`hidden md:flex min-h-11 items-center justify-center gap-2 border-none rounded-lg px-2 font-sans max-sm:text-[0.9375rem] text-sm font-normal leading-none text-[#727270] relative overflow-hidden ${
                  onStudyingClick
                    ? "cursor-pointer hover:text-main"
                    : "cursor-default"
                }`}
                onClick={onStudyingClick}
              >
                <span
                  className={`inline-flex items-center shrink-0 transition-all duration-[1.5s] ease-in-out ${
                    isAnimating ? "translate-y-2" : "translate-y-0"
                  }`}
                >
                  {studyingItems[currentItemIndex].icon}
                </span>
                <span className="relative inline-block min-w-[120px] text-left">
                  <span
                    className={`inline-block transition-all duration-[1.5s] ease-in-out ${
                      isAnimating ? "translate-y-2" : "translate-y-0"
                    }`}
                  >
                    {studyingItems[currentItemIndex].message}
                  </span>
                </span>
              </button>
            </Tooltip>
          )}

          {/* 2. ご意見ボックス（一時非表示） */}
          {/* <span className="hidden md:inline-flex">
            <FeedbackLink />
          </span> */}

          {/* 3. ブックマークボタン（ログイン時のみ） */}
          {userEmail && onBookmarkToggle && (
            <button
              onClick={onBookmarkToggle}
              className={`hidden md:flex items-center justify-center w-10 h-10 rounded-full border border-solid transition-colors cursor-pointer ${
                isBookmarked
                  ? "border-transparent bg-transparent text-primary"
                  : "border-main hover:bg-second text-sub"
              }`}
              aria-label={
                isBookmarked ? "ブックマーク解除" : "ブックマークに追加"
              }
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={isBookmarked ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </button>
          )}

          {/* 4. メモボタン（ログイン時のみ） */}
          {userEmail && onNotesClick && (
            <button
              onClick={onNotesClick}
              className={`hidden md:flex items-center justify-center w-10 h-10 rounded-full border border-solid transition-colors cursor-pointer relative ${
                hasNotes
                  ? "border-transparent bg-transparent text-primary"
                  : "border-main hover:bg-second text-sub"
              }`}
              aria-label="メモを開く"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              {hasNotes && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
              )}
            </button>
          )}

          {/* 5. 設定ボタン & ドロップダウンメニュー。
              フォントサイズ・テーマ・キーマップはエディター本体の設定なので
              CodingEditorArea 側のツールバーに移した。ここは図解の自動再生のみ */}
          {showSettings && (
          <div className="relative hidden md:block" ref={settingsMenuRef}>
              <button
                onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                className="flex items-center justify-center w-11 h-11 rounded-full border border-solid border-main hover:bg-second transition-colors cursor-pointer"
                aria-label="設定"
              >
                <SettingsIcon className="w-4 h-4 text-primary" />
              </button>

              {/* 設定ドロップダウンメニュー */}
              {isSettingsMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-surface rounded-xl shadow-lg border border-main overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-main">
                    <span className="font-bold max-sm:text-[0.9375rem] text-sm text-main">
                      レッスンの設定
                    </span>
                  </div>
                  <div className="py-1">
                    {/* 図解の自動再生。既定は入で、切ると触るまで動かなくなる */}
                    <button
                      onClick={() => setFigureAutoplay(!figureAutoplay)}
                      aria-pressed={figureAutoplay}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-second transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className="w-5 h-5 text-sub"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM10 8.5l6 3.5-6 3.5v-7z"
                          />
                        </svg>
                        <span className="max-sm:text-[0.9375rem] text-sm text-main">図解の自動再生</span>
                      </div>
                      <span
                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                          figureAutoplay ? "bg-primary" : "bg-third"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-main transition-all ${
                            figureAutoplay ? "left-[1.375rem]" : "left-0.5"
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                </div>
              )}
          </div>
          )}

          {/* 4. ユーザーアバター / ログイン・新規登録ボタン */}
          {userName || userEmail ? (
            <div className="relative">
              <UserAvatarButton
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="bg-main"
              />

              <UserProfileMenu
                isOpen={isUserMenuOpen}
                onClose={() => setIsUserMenuOpen(false)}
                userName={userName}
                userLevel={userLevel}
                userRank={userRank}
                userTotalXp={userTotalXp}
                userXpToNextLevel={userXpToNextLevel}
                LinkComponent={LinkComponent}
                onProfileClick={onProfileClick}
                onPlanClick={onPlanClick}
                onHelpClick={onHelpClick}
                onLogoutClick={onLogoutClick}
              />
            </div>
          ) : (
            /*
            戻り先を付ける。**付けないと、レッスンを読んでいた人が
            登録した瞬間に /home へ飛ばされて、読んでいた回を自分で
            探し直すことになる。** auth 側は getSafeRedirectPath で
            受けるので、外部URLは弾かれる
            */
            <div className="flex shrink-0 items-center gap-1 sm:gap-3">
              {/* 札の字は折らない (whitespace-nowrap)。狭い帯では札が縮んで「ログ/イン」と語の途中で折れていた。
                  縮める余地は隣のつまみ (headerAction) の側で取り、札は字の幅を守る */}
              <a href={withRedirect("/login")} className="shrink-0 no-underline">
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-h-11 whitespace-nowrap px-2 text-main hover:text-primary sm:px-3"
                >
                  ログイン
                </Button>
              </a>
              <a href={withRedirect("/signup")} className="shrink-0 no-underline">
                <Button variant="primary" size="sm" className="min-h-11 whitespace-nowrap px-2.5 sm:px-3">
                  新規登録
                </Button>
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Sidebar Menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          data-menu-overlay={menu ? "" : undefined}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuOpen(false);
          }}
        />
      )}
      <motion.nav
        initial={false}
        animate={isMenuOpen ? "open" : "closed"}
        ref={containerRef}
        id={menu ? menuId : undefined}
        role={menu && isMenuOpen ? "dialog" : undefined}
        aria-modal={menu && isMenuOpen ? true : undefined}
        aria-label={menu?.title}
        aria-hidden={menu ? !isMenuOpen : undefined}
        inert={menu && !isMenuOpen ? true : undefined}
        tabIndex={menu ? -1 : undefined}
        onClick={menu ? (event) => {
          if (event.target instanceof Element && event.target.closest("a[href]")) setIsMenuOpen(false);
        } : undefined}
        className={`fixed top-0 left-0 w-full md:w-[400px] ${menu ? "h-dvh" : "h-screen"} z-50 pointer-events-none`}
        style={{ pointerEvents: isMenuOpen ? "auto" : "none" }}
      >
        <motion.div
          className={`bg-[#fefef9] absolute top-0 left-0 h-full w-full md:w-[400px] ${menu ? "" : "shadow-2xl"} overflow-y-auto pointer-events-auto`}
          variants={sidebarVariants}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className={menu ? "flex-1 min-w-0" : "flex-1"}>
                <h2 className="font-bold text-lg md:text-2xl text-[#363635] mb-2">
                  {menu?.title ?? (course?.title || "コース")}
                </h2>
                {!menu && course?.description && (
                  <p className="max-sm:text-[0.9375rem] text-sm text-[#727270]">{course.description}</p>
                )}
              </div>
              {menu ? (
                <Button
                  variant="ghost"
                  onClick={() => setIsMenuOpen(false)}
                  className="h-11 w-11! min-w-11 max-w-11 shrink-0 px-0! py-0! bg-transparent! text-[#727270] hover:text-[#363635] focus:shadow-none! focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  aria-label="メニューを閉じる"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </Button>
              ) : <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMenuOpen(false);
                }}
                className="flex items-center justify-center w-11 h-11 bg-transparent border-none cursor-pointer text-[#727270] hover:text-[#363635] transition-colors"
                aria-label="メニューを閉じる"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>}
            </div>

            {/* Sections and Lessons - All visible with animation */}
            {menu ? menu.content : <motion.div
              className="flex flex-col gap-4"
              initial="closed"
              animate="open"
              variants={navVariants}
            >
              {course?.sections?.map((section) => {
                return (
                  <motion.div
                    key={section.stepNumber}
                    className="flex flex-col gap-2"
                    variants={itemVariants}
                  >
                    {/* Section Header */}
                    <div className="flex items-center gap-3 px-2 py-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary">
                        <span className="font-bold max-sm:text-[0.9375rem] text-xs text-white">
                          {section.stepNumber}
                        </span>
                      </div>
                      <span className="font-semibold max-sm:text-[0.9375rem] text-sm text-[#363635]">
                        {section.title}
                      </span>
                    </div>

                    {/* Lessons */}
                    <div className="flex flex-col gap-1 ml-4 pl-4 border-l-2 border-[#DCDCD9]">
                      {section.lessons.map((lesson) => {
                        const isCurrent = lesson.status === "current";
                        const isCompleted = lesson.status === "completed";
                        const isLocked = lesson.status === "locked";
                        const lessonUrl =
                          courseSlug && !isLocked && lesson.slug
                            ? `/learn/courses/${courseSlug}/${lesson.slug}`
                            : undefined;

                        return (
                          /*
                          押せる面は行そのものではなくリンク側にある。
                          リンクが 20px しか無いと、指で押したとき隣の回へ
                          飛ぶ。**器の padding を厚くしても押せる面は
                          広がらない** ので、下の LinkComponent と button に
                          min-h を入れている。ここは行間だけを持つ
                          */
                          <div
                            key={lesson.id}
                            className="flex items-center gap-3 py-0.5"
                          >
                            {/* Progress Indicator */}
                            <div className="relative flex items-center justify-center w-4 h-4">
                              {isCompleted ? (
                                <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                  >
                                    <path
                                      d="M2 6L5 9L10 3"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              ) : isCurrent ? (
                                <div className="w-4 h-4 rounded-full bg-primary" />
                              ) : (
                                <div className="w-4 h-4 rounded-full bg-[#DCDCD9]" />
                              )}
                            </div>

                            {/* Lesson Icon */}
                            <div className="flex items-center justify-center w-5 h-5">
                              {course?.mark ? (
                                <LanguageIcon language={course.mark.language} size={22} />
                              ) : lesson.type === "code" ? (
                                <CodeIcon color="#727270" />
                              ) : lesson.type === "quiz" ? (
                                <QuizIcon color="#727270" />
                              ) : (
                                <FileTextIcon color="#727270" />
                              )}
                            </div>

                            {/* Lesson Info */}
                            {lessonUrl && LinkComponent && !onLessonClick ? (
                              <LinkComponent
                                to={lessonUrl}
                                prefetch="intent"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setIsMenuOpen(false);
                                }}
                                className={`flex-1 flex min-h-[44px] items-center justify-between rounded-lg px-2 -mx-2 ${
                                  isCurrent
                                    ? "text-primary font-semibold"
                                    : isLocked
                                      ? "text-[#DCDCD9]"
                                      : "text-[#363635]"
                                } hover:text-primary transition-colors no-underline`}
                              >
                                <span className="max-sm:text-[0.9375rem] text-sm">
                                  {lesson.number}. {lesson.title}
                                </span>
                                <span className="max-sm:text-[0.9375rem] text-xs text-[#727270]">
                                  {lesson.duration}分
                                </span>
                              </LinkComponent>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isLocked) {
                                    setIsMenuOpen(false);
                                    onLessonClick?.(lesson.id);
                                  }
                                }}
                                disabled={isLocked}
                                className={`flex-1 flex min-h-[44px] items-center justify-between rounded-lg px-2 -mx-2 text-left ${
                                  isCurrent
                                    ? "text-primary font-semibold"
                                    : isLocked
                                      ? "text-[#DCDCD9] cursor-not-allowed"
                                      : "text-[#363635]"
                                } hover:text-primary transition-colors`}
                              >
                                <span className="max-sm:text-[0.9375rem] text-sm">
                                  {lesson.number}. {lesson.title}
                                </span>
                                <span className="max-sm:text-[0.9375rem] text-xs text-[#727270]">
                                  {lesson.duration}分
                                </span>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>}
          </div>
        </motion.div>
      </motion.nav>
    </>
  );
}
